import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherCard from 'features/Weather/components/WeatherCard/WeatherCard';
import useLocation from 'hooks/useLocation';
import { useMemo, useState } from 'react';
import { useGetSkiReportsQuery } from 'services/weather/ski/ski.api';
import SpecialReportWidgetSelect from '../../components/SpecialReportWidgetSelect/SpecialReportWidgetSelect';
import SpecialReportWidgetState from '../../components/SpecialReportWidgetState/SpecialReportWidgetState';
import '../../assets/specialReportWidgets.scss';
import { skiReportSections } from '../../features/SkiReports/config/skiReports.config';
import { hasSkiReportValue } from '../../features/SkiReports/utils/skiReports.utils';
import useSpecialReportWidgetRefresh from '../../hooks/useSpecialReportWidgetRefresh';

// Wide widgets put controls beside the card; tall widgets leave more room for long values.
const skiWidgetLayouts = {
  '1x1': { contentClassName: '', controlsClassName: 'special-report-widget-controls-paired' },
  '1x2': { contentClassName: '', controlsClassName: '' },
  '2x1': { contentClassName: 'special-report-widget-content-wide', controlsClassName: '' },
};
const sectionOptions = skiReportSections.map(({ id, title }) => ({ value: id, label: title }));

/** Loads ski reports and shows one resort and report section at a time. */
const SkiReportsWidget = ({ layout = '1x1', dimensions, widgetRefresh }) => {
  const { locations } = useLocation();
  const location = locations.effectiveLocation;
  const isLocationLoading = !location || location.isLoading;
  const { data, isLoading, isFetching, isError, refetch } = useGetSkiReportsQuery(location, {
    skip: isLocationLoading,
  });

  useSpecialReportWidgetRefresh({
    route: 'getSkiReports',
    location,
    widgetRefresh,
    refetch,
    skip: isLocationLoading,
  });

  const [selectedResortValue, setSelectedResortValue] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState(skiReportSections[0].id);
  const layoutConfig = skiWidgetLayouts[layout] ?? skiWidgetLayouts['1x1'];
  const resortOptions = useMemo(() => {
    const nameCounts = new Map();

    return (Array.isArray(data) ? data.filter(Boolean) : []).map((report) => {
      const name = hasSkiReportValue(report.ski_area_name)
        ? report.ski_area_name
        : 'Unnamed ski resort';
      const occurrence = nameCounts.get(name) ?? 0;
      nameCounts.set(name, occurrence + 1);

      // Reports have no resort ID. The name keeps selection stable when results reorder;
      // the occurrence keeps separate reports selectable when their names are identical.
      return {
        value: JSON.stringify([name, occurrence]),
        label: occurrence ? `${name} (${occurrence + 1})` : name,
        report,
      };
    });
  }, [data]);

  // A refresh may remove the selected resort, so immediately show the first remaining result.
  const selectedResort =
    resortOptions.find(({ value }) => value === selectedResortValue) ?? resortOptions[0];
  const selectedSection =
    skiReportSections.find(({ id }) => id === selectedSectionId) ?? skiReportSections[0];
  const { title, Icon, details, emptyMessage } = selectedSection;
  const availableDetails = selectedResort
    ? details.filter((detail) => detail.getValue(selectedResort.report) !== null)
    : [];

  // Keep the selected resort visible while a background refresh is running.
  if (isLocationLoading || ((isLoading || isFetching) && !data)) {
    return <WidgetLoadingAnimation widgetName="skiReports" dimensions={dimensions} />;
  }

  return (
    <>
      <WidgetTimeUpdate route="getSkiReports" realtime compact />
      <section
        className="special-report-widget d-flex flex-column h-100 min-h-0 gap-2 p-2"
        data-layout={layout}
        aria-label="Ski reports"
      >
        <SpecialReportWidgetState
          title="Ski Reports"
          isFetching={isFetching}
          isError={isError}
          hasData={Boolean(selectedResort)}
          onRetry={refetch}
          emptyMessage="No ski reports are available for this location."
        >
          {selectedResort && (
            <div className={`special-report-widget-content ${layoutConfig.contentClassName}`}>
              <div className={`special-report-widget-controls ${layoutConfig.controlsClassName}`}>
                <SpecialReportWidgetSelect
                  label="Resort"
                  options={resortOptions}
                  selectedValue={selectedResort.value}
                  onSelect={setSelectedResortValue}
                />
                <SpecialReportWidgetSelect
                  label="Report section"
                  options={sectionOptions}
                  selectedValue={selectedSection.id}
                  onSelect={setSelectedSectionId}
                />
              </div>
              <div className="special-report-widget-scroll">
                <WeatherCard
                  weatherObj={selectedResort.report}
                  title={title}
                  headingLevel="h3"
                  headerIcon={<Icon size={20} aria-hidden="true" />}
                  details={availableDetails}
                  emptyMessage={emptyMessage}
                />
              </div>
            </div>
          )}
        </SpecialReportWidgetState>
      </section>
    </>
  );
};

export default SkiReportsWidget;
